/* eslint-disable quotes */
/* eslint-disable no-use-before-define */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { FormProvider, useForm, Validate } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { UserApi } from 'mvppoker-portal-common/api'
import ReactCodeInput from 'react-code-input'
import { toast } from 'react-hot-toast'
import { useNavigate } from '../../navigation'
import { MVPButton } from '../../../Views/Buttons/MVPButton'
import { FullPageHeader } from '../../../Views/FullPageHeader/FullPageHeader'
import { MVPTextInput } from '../../../Views/TextInput/MVPTextInput'
import './ForgotPasswordEnterCodePage.scss'
import { handleError, useApi } from '../../../utils/useApi'
import { forgotPasswordEmailChanged } from '../../../state/reducers/activeUser'
import { RootState } from '../../../state/AppStore'

type RecoverPasswordFormData = {
	password: string
	password2: string
	code: string
}

export default () => {
	const form = useForm<RecoverPasswordFormData>({})
	const goToLogin = useNavigate('login')
	const dispatch = useDispatch()
	const [isLoading, setLoading] = useState(false)
	const userApi = useApi(UserApi)
	const email = useSelector((state: RootState) => state.activeUser.recoverPasswordEmail)

	useEffect(() => {
		if (!email) {
			goToLogin()
		}
	}, [])

	const setCode = useCallback(
		(value: string) => {
			form.setValue('code', value)
		},
		[form],
	)

	const onSubmit = ({ password, code }: RecoverPasswordFormData) => {
		setLoading(true)
		userApi
			.authUserForgotPasswordConfirm({
				username: email!,
				password,
				code,
			})
			.then(() => {
				goToLogin()
				toast.success('Password Changed Successfully!')
				dispatch(forgotPasswordEmailChanged(undefined))
			})
			.catch((err) => {
				handleError(err)
				setLoading(false)
			})
	}

	const codeRegistration = form.register('code', {
		required: { value: true, message: 'Reset Code is required!' },
		minLength: { value: 6, message: 'Needs to be 6 characters long' },
		maxLength: { value: 6, message: 'Needs to be 6 characters long' },
	})

	const codeError = form.formState.errors.code?.message

	return (
		<div id='forgot-password-code-page' className='stretch-page'>
			<div className='middle-col'>
				<FullPageHeader title='Recover Password' />
				<FormProvider {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)}>
						<span className='prompt'>Please enter your new password and reset code that was sent to you below</span>
						<div id='code-input-wrapper'>
							<span className='code-input-label'>Reset Code</span>
							<br />
							<ReactCodeInput
								className={codeError ? 'is-error' : ''}
								type='text'
								fields={6}
								inputMode='numeric'
								placeholder='-'
								onChange={setCode}
								pattern={codeRegistration.pattern}
								ref={codeRegistration.ref}
								name={codeRegistration.name}
								inputStyle={{
									fontFamily: 'monospace',
									borderRadius: '6px',
									border: '1px solid white',
									margin: '4px',
									paddingLeft: '0.5rem',
									width: '36px',
									height: '42px',
									fontSize: '2rem',
									color: 'white',
									backgroundColor: 'transparent',
									boxShadow: '0 0 .1rem #fff, 0 0 .3rem #fff,inset 0 0 .1rem #fff',
								}}
							/>

							{!!codeError && (
								<>
									<br />
									<span className='code-input-error-label'>{codeError}</span>
								</>
							)}
						</div>
						<MVPTextInput
							formField='password'
							formValidation={{
								required: { value: true, message: 'Password is required!' },
								validate: {
									...getPasswordValidationRules(),
								},
							}}
							label='New Password'
							placeholder='Enter a new password'
							type='password'
						/>
						<MVPTextInput
							formField='password2'
							formValidation={{
								required: { value: true, message: 'Password is required!' },
								validate: {
									...getPasswordValidationRules(),
									passwordsMatch: (value) => value === form.watch().password || "Passwords don't match",
								},
							}}
							label='Repeat Password'
							placeholder='Enter your new password again'
							type='password'
						/>
						<MVPButton type='submit' loading={isLoading}>
							Confirm
						</MVPButton>
					</form>
				</FormProvider>
			</div>
		</div>
	)
}

function getPasswordValidationRules(): Record<string, Validate<string>> {
	return {
		has8Chars: (it: string) => it.length >= 8 || 'Minimum 8 characters',
		hasLessThan100Char: (it: string) => it.length < 100 || 'Maximum 8 characters',
		has1Uppercase: (it: string) => !![...it].find((it) => it >= 'A' && it <= 'Z') || 'Needs one uppercase letter',
		has1Number: (it: string) => !![...it].find((it) => it >= '0' && it <= '9') || 'Needs one number',
		has1SpecialChar: (it: string) =>
			!![...it].find((it) => '^$*.[]{}()?-"!@#%&/\\,><\':;|_~`+='.includes(it)) ||
			'Needs at least one special character (^$*.[]{}()?-"!@#%&/\\,><\':;|_~`+=)',
	}
}
